import * as React from 'react'

import type { IScreenAnchor } from './PhotoMarkup.store'
import { ISize } from './photoMarkupModel'

interface IProps {
  anchor: IScreenAnchor
  stageSize: ISize
  isMobileEnv: boolean
  children: React.ReactNode
}

const GAP_PX = 12
const EDGE_PX = 8

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

export default function PhotoMarkupFloatingToolbar({
  anchor,
  stageSize,
  isMobileEnv,
  children,
}: IProps): JSX.Element {
  const toolbarRef = React.useRef<HTMLDivElement>(null)
  const [size, setSize] = React.useState<ISize>({ width: 0, height: 0 })

  React.useLayoutEffect(() => {
    const element = toolbarRef.current
    if (!element) return
    const { offsetWidth: width, offsetHeight: height } = element
    if (width !== size.width || height !== size.height) {
      setSize({ width, height })
    }
  })

  const above = anchor.top - GAP_PX - size.height
  const top = clamp(
    above >= EDGE_PX ? above : anchor.bottom + GAP_PX,
    EDGE_PX,
    Math.max(EDGE_PX, stageSize.height - size.height - EDGE_PX),
  )

  if (isMobileEnv) {
    return (
      <div
        className="tw:pointer-events-none tw:absolute tw:inset-x-2 tw:z-20 tw:flex tw:justify-center"
        style={{ top }}
      >
        <div
          ref={toolbarRef}
          className="tw:pointer-events-auto tw:max-w-full tw:overflow-x-auto"
        >
          {children}
        </div>
      </div>
    )
  }

  const left = clamp(
    anchor.x - size.width / 2,
    EDGE_PX,
    Math.max(EDGE_PX, stageSize.width - size.width - EDGE_PX),
  )

  return (
    <div
      ref={toolbarRef}
      className="tw:pointer-events-auto tw:absolute tw:z-20"
      style={{ left, top }}
    >
      {children}
    </div>
  )
}
